import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, ArrowLeft, Search, Filter, Mail, Phone, Building2, Calendar,
  CheckCircle, Clock, XCircle, TrendingUp, RefreshCw, ChevronDown,
  BarChart3, PieChart, AlertTriangle, Send, Award
} from 'lucide-react';
import api from '../services/api';

const estadoConfig = {
  pendiente_activacion: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  activo: { label: 'Activo', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  expirado: { label: 'Expirado', color: 'bg-red-100 text-red-800', icon: XCircle },
  convertido: { label: 'Convertido', color: 'bg-blue-100 text-blue-800', icon: Award }
};

function AdminTrials() {
  const [trials, setTrials] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [showConvertirModal, setShowConvertirModal] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState('starter');
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, [filtroEstado]);

  const cargarDatos = async () => {
    try {
      const params = filtroEstado ? `?estado=${filtroEstado}` : '';
      const [trialsRes, statsRes] = await Promise.all([
        api.get(`/admin/trials${params}`),
        api.get('/admin/trials/stats')
      ]);
      setTrials(trialsRes.data.data);
      setStats(statsRes.data.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const reenviarEmail = async (trial) => {
    if (!window.confirm(`Reenviar email de activacion a ${trial.email}?`)) return;
    try {
      await api.post(`/admin/trials/${trial.id}/reenviar-email`, {});
      alert('Email reenviado correctamente');
    } catch (error) {
      alert('Error reenviando email');
    }
  };

  const convertirTrial = async () => {
    if (!selectedTrial) return;
    try {
      await api.post(`/admin/trials/${selectedTrial.id}/convertir`, { plan: planSeleccionado });
      setShowConvertirModal(false);
      setSelectedTrial(null);
      cargarDatos();
      alert('Trial convertido correctamente');
    } catch (error) {
      alert('Error convirtiendo trial');
    }
  };

  const actualizarNotas = async (trial, notas) => {
    try {
      await api.put(`/admin/trials/${trial.id}`, { notas_comerciales: notas });
    } catch (error) {
      console.error('Error guardando notas');
    }
  };

  const trialsFiltrados = trials.filter(t => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return t.email?.toLowerCase().includes(q) ||
           t.nombre_empresa?.toLowerCase().includes(q) ||
           t.nombre_contacto?.toLowerCase().includes(q);
  });

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold">Gestion de Trials</h1>
            </div>
          </div>
          <button onClick={cargarDatos} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{trials.length}</p>
                  <p className="text-sm text-gray-500">Total Trials</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.por_estado?.pendiente_activacion || 0}</p>
                  <p className="text-sm text-gray-500">Pendientes</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.por_estado?.activo || 0}</p>
                  <p className="text-sm text-gray-500">Activos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.por_estado?.convertido || 0}</p>
                  <p className="text-sm text-gray-500">Convertidos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.tasa_conversion}%</p>
                  <p className="text-sm text-gray-500">Conversion</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Graficos de origen */}
        {stats && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <PieChart className="w-4 h-4 text-gray-400" />
                Por Fuente
              </h3>
              <div className="space-y-2">
                {stats.por_fuente?.slice(0, 5).map((f, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600">{f.como_conocio || 'Sin especificar'}</span>
                    <span className="font-medium">{f.total}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-gray-400" />
                Por Sector
              </h3>
              <div className="space-y-2">
                {stats.por_sector?.slice(0, 5).map((s, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600">{s.sector || 'Sin especificar'}</span>
                    <span className="font-medium">{s.total}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                Por Flota
              </h3>
              <div className="space-y-2">
                {stats.por_flota?.slice(0, 5).map((f, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600">{f.tamanio_flota || 'Sin especificar'}</span>
                    <span className="font-medium">{f.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por email, empresa o contacto..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filtroEstado}
                onChange={e => setFiltroEstado(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value="pendiente_activacion">Pendiente activacion</option>
                <option value="activo">Activo</option>
                <option value="expirado">Expirado</option>
                <option value="convertido">Convertido</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabla de Trials */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flota</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registro</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fin Trial</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {trialsFiltrados.map(trial => {
                  const estado = trial.estado_calculado || trial.estado;
                  const config = estadoConfig[estado] || estadoConfig.pendiente_activacion;
                  const Icon = config.icon;
                  const diasRestantes = trial.dias_restantes ? Math.floor(trial.dias_restantes) : null;

                  return (
                    <tr key={trial.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{trial.nombre_empresa}</p>
                          <p className="text-sm text-gray-500">{trial.sector || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-gray-900">{trial.nombre_contacto}</p>
                          <p className="text-sm text-gray-500">{trial.email}</p>
                          {trial.telefono && (
                            <p className="text-sm text-gray-400">{trial.telefono}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">{trial.tamanio_flota || '-'}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm text-gray-900">{formatFecha(trial.fecha_registro)}</p>
                          <p className="text-xs text-gray-400">{trial.como_conocio || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm text-gray-900">{formatFecha(trial.trial_fin)}</p>
                          {diasRestantes !== null && diasRestantes > 0 && estado === 'activo' && (
                            <p className={`text-xs ${diasRestantes <= 3 ? 'text-red-500' : 'text-gray-400'}`}>
                              {diasRestantes} dias restantes
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                          <Icon className="w-3 h-3" />
                          {config.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {estado === 'pendiente_activacion' && (
                            <button
                              onClick={() => reenviarEmail(trial)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Reenviar email"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {(estado === 'activo' || estado === 'expirado') && (
                            <button
                              onClick={() => { setSelectedTrial(trial); setShowConvertirModal(true); }}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Convertir a cliente"
                            >
                              <Award className="w-4 h-4" />
                            </button>
                          )}
                          <a
                            href={`mailto:${trial.email}`}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Enviar email"
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                          {trial.telefono && (
                            <a
                              href={`tel:${trial.telefono}`}
                              className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                              title="Llamar"
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {trialsFiltrados.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No hay registros de trial</p>
            </div>
          )}
        </div>
      </main>

      {/* Modal Convertir */}
      {showConvertirModal && selectedTrial && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Convertir a Cliente</h2>
            <p className="text-gray-600 mb-4">
              Convertir <strong>{selectedTrial.nombre_empresa}</strong> a cliente de pago.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Seleccionar Plan</label>
              <select
                value={planSeleccionado}
                onChange={e => setPlanSeleccionado(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="starter">Starter</option>
                <option value="business">Business</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConvertirModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={convertirTrial}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Convertir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTrials;
